import { useEffect, useRef, useState } from "react";
import { useDelayedVisible } from "~/hooks/useDelayedVisible";
import { useNavigationPending } from "~/hooks/useNavigationPending";
import { usePendingAction } from "~/hooks/usePendingAction";

/** POST 開始からこの時間を超えて応答が無い場合にだけオーバーレイを出す */
const PROCESSING_OVERLAY_DELAY_MS = 3000;
/** 一度出したオーバーレイを最低これだけ表示し続ける (閾値直後の応答で点滅させない) */
const PROCESSING_OVERLAY_MIN_VISIBLE_MS = 600;
/** さらにこの時間を超えたら「時間がかかっている」旨の補足文言を足す */
const PROCESSING_OVERLAY_LONG_WAIT_MS = 15000;

// POST 通信 (clientAction 経由 / hooks からの直接 RPC) が長引いたときだけ画面全体をグレーアウトする。
// 通常の保存はボタンの pending 表示 (Button の isPending) で足りるため、閾値未満では何も出さない。
// モーダル (z-50) 上からの POST も覆う必要があるので z-[60] に置く。
export const ProcessingOverlay = () => {
    const isNavigationPending = useNavigationPending();
    const { isPending: isActionPending } = usePendingAction();
    const isPending = isNavigationPending || isActionPending;

    const isVisible = useDelayedVisible(isPending, {
        delayMs: PROCESSING_OVERLAY_DELAY_MS,
        minVisibleMs: PROCESSING_OVERLAY_MIN_VISIBLE_MS,
    });
    const [isLongWait, setIsLongWait] = useState(false);
    const overlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isPending) {
            setIsLongWait(false);
            return;
        }
        const timer = setTimeout(() => setIsLongWait(true), PROCESSING_OVERLAY_LONG_WAIT_MS);
        return () => clearTimeout(timer);
    }, [isPending]);

    // 表示中はキーボード操作も塞ぐ。fixed 要素はマウスしか覆わず、Tab で背後のボタンへ移動して Enter で
    // 別の POST を開始できてしまうため、フォーカスをオーバーレイに移し Tab を抑止する。閉じたら元の位置へ戻す。
    useEffect(() => {
        if (!isVisible) return;
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        overlayRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Tab") return;
            event.preventDefault();
            overlayRef.current?.focus();
        };
        document.addEventListener("keydown", handleKeyDown, true);
        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
            previouslyFocused?.focus();
        };
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div
            ref={overlayRef}
            tabIndex={-1}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/30 outline-none"
        >
            <div className="flex min-w-64 items-center gap-4 rounded-md bg-white px-6 py-4 shadow-xl">
                <div className="size-7 shrink-0 animate-spin rounded-full border-[3px] border-blue-600 border-t-transparent motion-reduce:animate-[spin_2.4s_linear_infinite]" />
                <output aria-live="polite" className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">処理中...</span>
                    <span className="text-xs text-gray-500">
                        {isLongWait
                            ? "時間がかかっています。画面を閉じずにお待ちください"
                            : "完了するまでお待ちください"}
                    </span>
                </output>
            </div>
        </div>
    );
};
